import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { InputAlert } from '../global/input-alert/input-alert';
import { ArmyBuilderConfig } from '../global/config';
import { isVersionSupported } from '../global/utils';

import { Force, Unit, UnitTemplate, Faction, ValidationError, UnitOption } from './models';

export abstract class ForceUtils {
    abstract gameId: string;
    protected translateService?: TranslateService;
    protected inputAlert?: InputAlert;
    protected config = inject(ArmyBuilderConfig);

    getForceCost(force: Force): number {
        // This returns an Observable<number> because some games rely
        // on the current settings to calculate the cost of the force.
        const unitCosts = force.units.map((u) => this.getUnitCost(u, force));

        return unitCosts.reduce((total, cost) => {
            console.log('!!!!! Total cost', total, cost);
            return total + cost;
        }, 0);
    }

    attachCostToForce(f: Force) {
        return f.error
            ? f
            : {
                  ...f,
                  cost: this.getForceCost(f)
              };
    }

    getDefaultForce(forceType: Faction): Omit<Force, 'id'> {
        return {
            faction: forceType.key,
            gameId: this.gameId,
            name: '',
            units: [],
            cost: 0,
            platoons: [],
            derived: undefined
        };
    }

    abstract validateForce(force: Force): ValidationError[];

    getForceFromServer(forceId: string): Observable<Force> {
        throw new Error('getForceFromServer not implemented');
    }

    // renamePlatoon(force: Force, platoonIndex: number): Promise<Force> {
    //     return new Promise((resolve, _reject) => {
    //         snapshot(
    //             combineLatest([
    //                 this.translateService.get('FORCES.DIALOGS.PLATOON_NAME.TITLE'),
    //                 this.translateService.get('FORCES.DIALOGS.PLATOON_NAME.MESSAGE')
    //             ]),
    //             ([title, message]) => {
    //                 this.inputAlert.show(title, message, force.platoons[platoonIndex] || '').then((val) => {
    //                     if (!val) {
    //                         return;
    //                     }
    //                     const platoons = [...force.platoons];
    //                     platoons[platoonIndex] = val;
    //                     force.platoons = platoons;
    //                     resolve(force);
    //                 });
    //             }
    //         );
    //     });
    // }

    abstract getUnitCost(unit: Unit | UnitTemplate, force?: Force, dataBucket?: any): number;
    abstract createUnitFromTemplate(unitTemplate: UnitTemplate, dataBucket?: any): Unit;
    abstract processForce(force: Force): Promise<Force>;
    abstract canUnitReceiveUpgrade(
        unit: Unit,
        force: Force,
        upgrade: UnitOption
    ): { canReceiveUpgrade: boolean; errorMessage?: { headerKey: string; messageKey: string } };

    /**
     * Returns true if the force was saved by an older version of the app and its
     * derived data needs to be recalculated and persisted.
     *
     * When this returns true the force is reprocessed, and the updated `derived`
     * object and current `appVersion` are written back to the store (triggering a
     * sync).  Subsequent loads will see the bumped `appVersion` and skip migration.
     *
     * The default implementation triggers migration whenever `force.appVersion` is
     * older than the current app version.  Override in a game-specific ForceUtils
     * to target a specific minimum version (e.g. the version that introduced a new
     * derived field) so that forces are not unnecessarily reprocessed on every
     * version bump.
     */
    shouldUpdateDerivedData(force: Force): boolean {
        return !isVersionSupported(force.appVersion ?? '0.0.0', this.config.version);
    }

    preProcessForce(force: Force, unitTemplates: UnitTemplate[], forceId: string): Force {
        const unitsBeforeFilter = force.units.map(this.attachUnitTemplate(unitTemplates));
        const units = unitsBeforeFilter.filter((u) => !!u);

        if (units.length !== force.units.length) {
            console.log('[FORCE-UTILS] preProcessForce filtered out units!', {
                forceId: force.id,
                originalUnitCount: force.units.length,
                originalUnitIds: force.units.map((u) => u.id),
                afterFilterCount: units.length,
                afterFilterIds: units.map((u) => u.id),
                filteredOutIds: force.units.filter((u) => !units.find((fu) => fu?.id === u.id)).map((u) => u.id)
            });
        }

        return {
            ...structuredClone(force),
            validationErrors: [],
            units,
            selected: force.id === forceId
        };
    }

    postProcessForce(force: Force) {
        let f = this.attachCostToForce(force);
        if (f.error) {
            return f; // Don't bother with validation if the force isn't even loading}
        }
        const validationErrors = this.validateForce(f);
        f.validationErrors = validationErrors;

        if (f.platoons) {
            // Some games (e.g. Bolt Action) subdivide forces, which we call platoons
            f.platoons = f.platoons.map((p) => {
                return {
                    ...p,
                    validationErrors: validationErrors.filter((e) => e.platoonId === p.id)
                };
            });
        }

        return f;
    }

    attachUnitTemplate(unitTemplates: UnitTemplate[]) {
        return (unit) => {
            const unitTemplate = unitTemplates.find((u) => u._id === unit.unitTemplateId);
            if (!unitTemplate) {
                return null;
            }

            return {
                ...unit,
                unitTemplate
            };
        };
    }
}
